<template>
  <lb-dialog
    :value="value"
    heading="Export Manager"
    width="60vw"
    content-class="export-manager-dialog"
    data-testid="export-manager-dialog"
    @input="onDialogInput"
  >
    <template #body>
      <div class="export-manager-dialog__body" data-testid="export-manager-dialog__body">
        <div class="export-manager-dialog__intro">
          <div class="export-manager-dialog__header-row">
            <div class="export-manager-dialog__intro-copy">
              <div class="export-manager-dialog__title-group">
                <div class="export-manager-dialog__section-title">Export history</div>
                <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      small
                      class="shadow-off"
                      :disabled="isHistoryBusy"
                      :loading="isHistoryBusy"
                      data-testid="export-manager-dialog__refresh-btn"
                      @click="onRefresh"
                    >
                      <img
                        src="/img/icons/fbc-icons/RefreshIcon.svg"
                        alt="Refresh"
                        class="export-manager-dialog__refresh-icon"
                      >
                    </v-btn>
                  </template>
                  <span>Refresh</span>
                </v-tooltip>
              </div>
              <p class="export-manager-dialog__instruction">
                Generate an export of this cycle's activity log or files. It is prepared in the background, so you can close this window and download it from here when it's ready.
              </p>
            </div>
            <div
              v-if="canExport || hasActiveFilters"
              class="export-manager-dialog__actions d-flex align-center"
            >
              <v-btn
                v-if="hasActiveFilters"
                text
                class="export-manager-dialog__clear-filter-btn text-none"
                data-testid="export-manager-dialog__clear-filter-btn"
                @click="onClearFilters"
              >
                Clear Filter
              </v-btn>
              <v-menu
                v-if="canExport"
                v-model="exportMenu"
                offset-y
                left
                :nudge-bottom="6"
                content-class="export-manager-dialog__export-menu"
                transition="fade-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    class="export-manager-dialog__export-btn text-none"
                    :disabled="exporting"
                    :loading="exporting"
                    data-testid="export-manager-dialog__export-btn"
                  >
                    <span data-testid="export-manager-dialog__export-label">Export</span>
                    <v-icon class="ml-1" small>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <div class="export-manager-dialog__export-menu-panel" data-testid="export-manager-dialog__export-menu">
                  <p
                    class="export-manager-dialog__export-menu-label ma-0"
                    data-testid="export-manager-dialog__export-menu-label"
                  >
                    Select type
                  </p>
                  <button
                    type="button"
                    class="export-manager-dialog__export-menu-item"
                    data-testid="export-manager-dialog__activity-log-btn"
                    :disabled="exporting"
                    @click="onExport(activityLogType)"
                  >
                    {{ activityLogExportLabel }}
                  </button>
                  <button
                    v-if="canExportFile"
                    type="button"
                    class="export-manager-dialog__export-menu-item"
                    data-testid="export-manager-dialog__file-btn"
                    :disabled="exporting"
                    @click="onExport(fileType)"
                  >
                    {{ fileExportLabel }}
                  </button>
                </div>
              </v-menu>
            </div>
          </div>
        </div>

        <div
          class="export-manager-dialog__table flex-fill-table"
          :class="{ 'export-manager-dialog__table--refreshing': isHistoryBusy && !showSkeleton }"
          :aria-busy="String(isHistoryBusy)"
          data-testid="export-manager-dialog__history"
        >
          <lb-datatableaction
            ref="dataTableAction"
            class="fbc-table-header-labels"
            :headers="headers"
            :tabledata="tableRows"
            :displayed-row-count="showSkeleton ? 1 : filteredRows.length"
            :bulk-actions="false"
            :show-toolbar="false"
            :selectfilter="activeColumnFilters"
            :page_details="pageDetails"
            :enable-cell-truncate="false"
            header-bg-class="export-manager-dialog__history-header"
            data-testid="export-manager-dialog__datatable"
            @filter-item="onFilterItem"
            @filter-active-change="onFilterActiveChange"
            @clear-item="onClearItem"
          >
            <template #tbody="{ headers: slotHeaders }">
              <template v-if="showSkeleton">
                <tr>
                  <td
                    :colspan="visibleHeaders(slotHeaders).length"
                    class="export-manager-dialog__skeleton-cell"
                  >
                    <div
                      class="export-manager-dialog__history-skeleton"
                      data-testid="export-manager-dialog__history-skeleton"
                    >
                      <v-skeleton-loader type="table-row-divider@4" />
                    </div>
                  </td>
                </tr>
              </template>
              <tr
                v-for="(row, index) in filteredRows"
                v-else
                :key="row.exportId"
                :data-testid="`export-manager-dialog__row--${index}`"
              >
                <template v-for="header in visibleHeaders(slotHeaders)">
                  <td
                    v-if="header.value === 'exportTypeLabel'"
                    :key="`${header.value}-${row.exportId}`"
                    class="export-manager-dialog__cell"
                    :data-testid="`export-manager-dialog__type-cell--${index}`"
                  >
                    <span class="export-manager-dialog__type">{{ row.exportTypeLabel }}</span>
                  </td>
                  <td
                    v-else-if="header.value === 'requestedAt'"
                    :key="`${header.value}-${row.exportId}`"
                    class="export-manager-dialog__cell"
                    :data-testid="`export-manager-dialog__requested-cell--${index}`"
                  >
                    {{ formatRequestedOn(row.requestedAt) }}
                  </td>
                  <td
                    v-else-if="header.value === 'statusLabel'"
                    :key="`${header.value}-${row.exportId}`"
                    class="export-manager-dialog__cell"
                    :data-testid="`export-manager-dialog__status-cell--${index}`"
                  >
                    <span v-if="isGenerating(row.stage)" class="export-manager-dialog__generating">
                      <span class="export-manager-dialog__spinner" aria-hidden="true">
                        <img src="/img/icons/fbc-icons/ExportManagerSpinner.svg" alt="" />
                      </span>
                      <span>{{ statusGenerating }}</span>
                    </span>
                    <span v-else-if="isReady(row.stage)" class="export-manager-dialog__ready">{{ statusReady }}</span>
                    <span v-else-if="isFailed(row.stage)" class="export-manager-dialog__failed">
                      <span :data-testid="`export-manager-dialog__failed-status--${index}`">{{ statusFailed }}</span>
                      <FcTooltip
                        v-if="row.error"
                        :text="row.error"
                        placement="bottom"
                        fixed
                        eager
                        focusable
                        transition="fade-transition"
                        :open-delay="0"
                        activator-class="export-manager-dialog__failed-info"
                        content-class="export-manager-dialog__failed-tooltip"
                      >
                        <v-icon
                          class="export-manager-dialog__failed-info-icon"
                          aria-hidden="true"
                          :data-testid="`export-manager-dialog__failed-info-icon--${index}`"
                        >mdi-information-outline</v-icon>
                        <span class="d-sr-only">{{ row.error }}</span>
                      </FcTooltip>
                    </span>
                  </td>
                  <td
                    v-else-if="header.value === 'action'"
                    :key="`${header.value}-${row.exportId}`"
                    class="export-manager-dialog__cell export-manager-dialog__cell--action"
                    :data-testid="`export-manager-dialog__action-cell--${index}`"
                  >
                    <button
                      v-if="isReady(row.stage)"
                      type="button"
                      class="export-manager-dialog__download-btn"
                      :data-testid="`export-manager-dialog__download-btn--${index}`"
                      @click="onDownload(row)"
                    >
                      <img src="/img/icons/fbc-icons/ExportManagerDownload.svg" alt="" />
                      <span>Download</span>
                    </button>
                    <span v-else class="export-manager-dialog__pending">{{ actionPending }}</span>
                  </td>
                </template>
              </tr>
            </template>
          </lb-datatableaction>
        </div>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import './ExportManagerDialog.scss';
import { mapActions } from 'vuex';
import { FcTooltip } from '../../../../components';
import { ACTIVITY_LOG_EXPORT } from '../../../../utils/constants';
import {
  isGeneratingStage,
  isReadyStage,
  isFailedStage,
  mergeExportHistory,
  normalizeExportRow,
  openSignedExportUrl,
  toExportTableRow,
} from '../../../../utils/activityLogExport';

export default {
  name: 'ExportManagerDialog',

  components: {
    FcTooltip,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    cycleId: {
      type: String,
      default: '',
    },
    canExport: {
      type: Boolean,
      default: true,
    },
    canExportFile: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      history: [],
      exporting: false,
      loadingHistory: false,
      refreshing: false,
      exportMenu: false,
      pollTimer: null,
      historyRequestId: 0,
      isDestroyed: false,
      headers: ACTIVITY_LOG_EXPORT.HISTORY_HEADERS.map((header) => ({ ...header })),
      activeColumnFilters: {},
      hasActiveFilters: false,
      pageDetails: {},
      statusGenerating: ACTIVITY_LOG_EXPORT.STATUS_GENERATING,
      statusReady: ACTIVITY_LOG_EXPORT.STATUS_READY,
      statusFailed: ACTIVITY_LOG_EXPORT.STATUS_FAILED,
      actionPending: ACTIVITY_LOG_EXPORT.ACTION_PENDING,
      activityLogType: ACTIVITY_LOG_EXPORT.EXPORT_TYPE_ACTIVITY_LOG,
      fileType: ACTIVITY_LOG_EXPORT.EXPORT_TYPE_FILE,
      activityLogExportLabel: ACTIVITY_LOG_EXPORT.LABEL_ACTIVITY_LOG,
      fileExportLabel: ACTIVITY_LOG_EXPORT.LABEL_FILE,
    };
  },

  computed: {
    isHistoryBusy() {
      return this.loadingHistory || this.refreshing;
    },

    /** Skeleton only while there are no rows; existing rows stay in place during a reload. */
    showSkeleton() {
      return this.isHistoryBusy && !this.history.length;
    },

    tableRows() {
      return this.history.map((row) => toExportTableRow(row));
    },

    filteredRows() {
      const filters = this.activeColumnFilters || {};
      const hasFilters = Object.keys(filters).some((key) => {
        const value = filters[key];
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') {
          return Object.values(value).some((bound) => bound !== null && bound !== undefined && bound !== '');
        }
        return false;
      });
      if (!hasFilters || !this.$nova || !this.$nova.filterByDynamicCriteria) {
        return this.tableRows;
      }
      return this.$nova.filterByDynamicCriteria(this.tableRows, filters);
    },
  },

  watch: {
    value(open) {
      if (open) {
        console.log('[ExportManagerDialog]:', { cycleId: this.cycleId });
        this.openHistory();
      } else {
        this.stopPolling();
      }
    },
  },

  beforeDestroy() {
    this.isDestroyed = true;
    this.historyRequestId += 1;
    this.stopPolling();
    this.history = [];
    this.exporting = false;
    this.loadingHistory = false;
    this.refreshing = false;
    this.exportMenu = false;
    this.activeColumnFilters = {};
    this.hasActiveFilters = false;
  },

  methods: {
    ...mapActions([
      'requestCycleActivityLogExport',
      'fetchCycleActivityLogExports',
      'downloadCycleActivityLogExport',
    ]),

    onDialogInput(open) {
      this.$emit('input', open);
    },

    visibleHeaders(slotHeaders) {
      const source = slotHeaders && slotHeaders.length ? slotHeaders : this.headers;
      return source.filter((header) => header.visible !== false);
    },

    isGenerating(stage) {
      return isGeneratingStage(stage);
    },

    isReady(stage) {
      return isReadyStage(stage);
    },

    isFailed(stage) {
      return isFailedStage(stage);
    },

    formatRequestedOn(value) {
      if (!value || !this.$nova || !this.$nova.formatDateTime) return '';
      return this.$nova.formatDateTime(value, ACTIVITY_LOG_EXPORT.DATE_FORMAT);
    },

    onFilterItem(filters) {
      this.activeColumnFilters = filters || {};
    },

    onClearItem(filters) {
      this.activeColumnFilters = filters || {};
      this.hasActiveFilters = false;
    },

    onFilterActiveChange(isActive) {
      this.hasActiveFilters = Boolean(isActive);
    },

    onClearFilters() {
      const table = this.$refs.dataTableAction;
      if (table && typeof table.clearfilter === 'function') table.clearfilter();
    },

    syncPolling() {
      if (this.isDestroyed) return;
      const pending = this.history.some((row) => isGeneratingStage(row.stage));
      if (pending && this.value) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },

    startPolling() {
      if (this.isDestroyed || this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.loadHistory(true);
      }, ACTIVITY_LOG_EXPORT.POLL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async loadHistory(silent) {
      if (!this.cycleId || this.isDestroyed) return;
      const requestId = this.historyRequestId + 1;
      this.historyRequestId = requestId;
      try {
        const rows = await this.fetchCycleActivityLogExports({
          cycleId: this.cycleId,
          silent: Boolean(silent),
        });
        if (requestId !== this.historyRequestId || this.isDestroyed) return;
        this.history = mergeExportHistory(rows, this.history);
      } catch (error) {
        if (requestId !== this.historyRequestId || this.isDestroyed) return;
        console.log('[ExportManagerDialog]:', error);
      }
      if (requestId === this.historyRequestId && !this.isDestroyed) {
        this.syncPolling();
      }
    },

    async openHistory() {
      if (this.loadingHistory || !this.cycleId) return;
      this.loadingHistory = true;
      try {
        await this.loadHistory(false);
      } finally {
        if (!this.isDestroyed) this.loadingHistory = false;
      }
    },

    async onRefresh() {
      if (this.isHistoryBusy || !this.cycleId) return;
      this.refreshing = true;
      try {
        await this.loadHistory(false);
      } finally {
        this.refreshing = false;
      }
    },

    async onExport(exportType) {
      this.exportMenu = false;
      if (this.exporting || !this.cycleId) return;
      this.exporting = true;
      try {
        const result = await this.requestCycleActivityLogExport({
          cycleId: this.cycleId,
          exportType,
        });
        const optimistic = normalizeExportRow({
          exportId: result.exportId,
          stage: ACTIVITY_LOG_EXPORT.STAGE_QUEUED,
          requestedAt: new Date().toISOString(),
          exportType,
        });
        this.history = mergeExportHistory(this.history, [optimistic]);
        this.syncPolling();
        this.loadHistory(true);
      } catch (error) {
        console.log('[ExportManagerDialog]:', error);
      } finally {
        this.exporting = false;
      }
    },

    async onDownload(row) {
      if (!row || !isReadyStage(row.stage)) return;
      try {
        const result = await this.downloadCycleActivityLogExport({
          exportId: row.exportId,
          fileName: row.fileName,
        });
        openSignedExportUrl(result.url, result.fileName || row.fileName);
      } catch (error) {
        console.log('[ExportManagerDialog]:', error);
      }
    },
  },
};
</script>
